/**
 * Options Dashboard — /options-dashboard
 * Landing page for the Options Intelligence Module.
 * Shows: Market State Strip + Pulse Cards (one per tracked underlying) + Signal Card
 */

import React, { useEffect, useCallback, useMemo, useState } from 'react';
import {
  Box, Typography, Button, Skeleton, Alert, Chip,
  TextField, InputAdornment, ToggleButtonGroup, ToggleButton,
  Select, MenuItem, FormControl, InputLabel, Tooltip, IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScienceIcon from '@mui/icons-material/Science';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../store';
import {
  fetchPulseCards,
  clearActiveSignal
} from '../store/optionsSlice';
import MarketStateStrip from '../components/options/MarketStateStrip';
import PulseCard, { PulseTier } from '../components/options/PulseCard';
import SignalCard from '../components/options/SignalCard';
import ManageSymbolsDialog from '../components/options/ManageSymbolsDialog';
import { useAppTheme } from '../hooks/useAppTheme';

const OptionsDashboardPage: React.FC = () => {
  const { C } = useAppTheme();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { pulseCards, loading, error, activeSignal, activeBasket } = useSelector(
    (state: RootState) => state.options
  );

  const [searchText, setSearchText] = useState('');
  const [exchangeFilter, setExchangeFilter] = useState<string>(() =>
    localStorage.getItem('fno_dash_exchange') || 'ALL');
  const [signalFilter, setSignalFilter] = useState<string>(() =>
    localStorage.getItem('fno_dash_signal') || 'ALL');
  const [starFilter, setStarFilter] = useState<boolean>(() =>
    localStorage.getItem('fno_dash_star') !== '0'); // default ON
  const [groupFilter, setGroupFilter] = useState<string>(() =>
    localStorage.getItem('fno_dash_group') || 'ALL');
  // Persist filters
  useEffect(() => { localStorage.setItem('fno_dash_exchange', exchangeFilter); }, [exchangeFilter]);
  useEffect(() => { localStorage.setItem('fno_dash_signal', signalFilter); }, [signalFilter]);
  useEffect(() => { localStorage.setItem('fno_dash_star', starFilter ? '1' : '0'); }, [starFilter]);
  useEffect(() => { localStorage.setItem('fno_dash_group', groupFilter); }, [groupFilter]);

  // ── Watchlist groups ─────────────────────────────────────────────────────
  // Symbol → group mapping persisted to localStorage. Auto-classify by
  // exchange / well-known index names; user can override per symbol.
  const GROUPS = ['Indices', 'Banking', 'Stocks', 'Commodities', 'Crypto'];
  const INDEX_NAMES = new Set(['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'SENSEX', 'BANKEX', 'MIDCPNIFTY', 'NIFTYNXT50']);
  const BANK_NAMES = /HDFC|ICICI|AXIS|SBIN|KOTAK|INDUSIND|YESBANK|FEDERAL|PNB|BAJAJFINSV|BAJFINANCE/i;
  const autoGroup = (sym: string, exchange: string): string => {
    const u = sym.toUpperCase();
    if (INDEX_NAMES.has(u)) return 'Indices';
    if (exchange === 'MCX') return 'Commodities';
    if (exchange === 'DELTA') return 'Crypto';
    if (BANK_NAMES.test(u)) return 'Banking';
    return 'Stocks';
  };
  const [groupOverrides, setGroupOverrides] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem('fno_dash_groups') || '{}'); } catch { return {}; }
  });
  const groupOf = (card: { symbol: string; exchange: string }) =>
    groupOverrides[card.symbol] || autoGroup(card.symbol, card.exchange);
  const [groupEditOpen, setGroupEditOpen] = useState(false);
  const saveGroupOverride = (sym: string, group: string) => {
    setGroupOverrides(prev => {
      const next = { ...prev };
      if (autoGroup(sym, '') === group) delete next[sym];
      else next[sym] = group;
      try { localStorage.setItem('fno_dash_groups', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const [labDays, setLabDays] = useState<number>(() => {
    const saved = localStorage.getItem('fno_lab_days');
    return saved ? Number(saved) : 30;
  });
  const [manageOpen, setManageOpen] = useState(false);

  const load = useCallback(() => {
    dispatch(fetchPulseCards());
  }, [dispatch]);

  useEffect(() => {
    load();
    // Auto-refresh every 2 minutes
    const t = setInterval(load, 2 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const staleCount = pulseCards.filter(c => c.dataStale).length;

  const filteredCards = pulseCards.filter(card => {
    const q = searchText.toUpperCase().trim();
    if (q && !card.symbol.includes(q) && !card.displayName.toUpperCase().includes(q)) return false;
    if (exchangeFilter !== 'ALL' && card.exchange !== exchangeFilter) return false;
    if (signalFilter !== 'ALL') {
      const dir = card.latestSignal?.direction ?? null;
      if (!dir || dir !== signalFilter) return false;
    }
    if (groupFilter !== 'ALL' && groupOf(card) !== groupFilter) return false;
    // Star filter: high-conviction trade candidates — must have an active signal AND score ≥ 70
    if (starFilter) {
      const sig = card.latestSignal;
      if (!sig || !sig.direction) return false;  // no recent FNO signal → skip
      if (Number(sig.score) < 70) return false;  // low confidence → skip
    }
    return true;
  });

  // 3-tier sort: by conviction score desc; first 3 = hero, next 12 = standard, rest = compact.
  const tieredCards = useMemo(() => {
    const sorted = [...filteredCards].sort((a, b) => {
      const sa = Number(a.latestSignal?.score ?? 0);
      const sb = Number(b.latestSignal?.score ?? 0);
      return sb - sa;
    });
    return sorted.map((card, i) => {
      const score = Number(card.latestSignal?.score ?? 0);
      let tier: PulseTier;
      if (i < 3 && score >= 70) tier = 'hero';
      else if (i >= 15 && score < 50) tier = 'compact';
      else tier = 'standard';
      return { card, tier };
    });
  }, [filteredCards]);

  // Live straddle ticker — pinned strip of top 3 index ATM straddle premiums.
  const indexCards = useMemo(() => {
    const idx = pulseCards.filter(c => INDEX_NAMES.has(c.symbol.toUpperCase()) && c.latestMetrics);
    // Prefer Nifty, BankNifty, Sensex in that order; pad with whatever else
    const PREFERRED = ['NIFTY', 'BANKNIFTY', 'SENSEX'];
    const pick: typeof idx = [];
    for (const sym of PREFERRED) {
      const c = idx.find(x => x.symbol.toUpperCase() === sym);
      if (c) pick.push(c);
    }
    for (const c of idx) {
      if (pick.length >= 3) break;
      if (!pick.includes(c)) pick.push(c);
    }
    return pick.slice(0, 3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulseCards]);

  return (
    <Box>
      {/* Live straddle premium ticker — pinned at the very top for index awareness */}
      {indexCards.length > 0 && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, px: 1.25, py: 0.75,
          borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper', overflowX: 'auto',
        }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mr: 0.5, whiteSpace: 'nowrap' }}>
            Straddle:
          </Typography>
          {indexCards.map(c => {
            const m = c.latestMetrics!;
            const spot = Number(m.spotPrice || 0);
            const straddle = m.straddlePrice ? Number(m.straddlePrice) : null;
            const ivr = Number(m.ivRank52w || 0);
            const ivColor = ivr > 65 ? C.bear : ivr > 35 ? C.warn : C.bull;
            return (
              <Box
                key={c.id}
                onClick={() => navigate(`/options-lab/${c.symbol}`)}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 1, py: 0.4, borderRadius: 0.75, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <Typography variant="body2" sx={{ fontWeight: 800 }}>{c.symbol}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                  ₹{spot.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </Typography>
                {straddle != null && (
                  <Chip
                    size="small"
                    label={`₹${straddle.toFixed(0)}`}
                    sx={{ height: 18, fontSize: '0.66rem', fontWeight: 700, bgcolor: 'action.hover' }}
                  />
                )}
                <Chip
                  size="small"
                  label={`IVR ${ivr.toFixed(0)}`}
                  sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: `${ivColor}22`, color: ivColor }}
                />
              </Box>
            );
          })}
          <Box sx={{ flex: 1 }} />
        </Box>
      )}

      {/* Market State Strip */}
      <MarketStateStrip
        alertCount={activeSignal ? 1 : 0}
        dataWarning={staleCount > 0 ? `${staleCount} symbol(s) have stale data` : null}
        hasCrypto={pulseCards.some(c => c.feedSource === 'DELTA')}
      />

      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>FNO Intelligence</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Live option chain & futures analytics · Combined FNO scoring
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button onClick={() => navigate('/options?tab=trade')} size="small" variant="outlined">
            Build a trade
          </Button>
          <Button
            startIcon={<AccountTreeIcon />}
            onClick={() => navigate('/options')}
            size="small"
            variant="contained"
          >
            Options hub
          </Button>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Lab Period</InputLabel>
            <Select
              value={labDays}
              label="Lab Period"
              onChange={e => {
                const v = Number(e.target.value);
                setLabDays(v);
                localStorage.setItem('fno_lab_days', String(v));
              }}
            >
              <MenuItem value={1}>1 Day</MenuItem>
              <MenuItem value={3}>3 Days</MenuItem>
              <MenuItem value={5}>5 Days</MenuItem>
              <MenuItem value={10}>10 Days</MenuItem>
              <MenuItem value={15}>15 Days</MenuItem>
              <MenuItem value={20}>20 Days</MenuItem>
              <MenuItem value={30}>30 Days</MenuItem>
            </Select>
          </FormControl>
          <Button
            startIcon={<TuneIcon />}
            onClick={() => setManageOpen(true)}
            size="small"
            variant="outlined"
            sx={{ color: C.neutral, borderColor: C.neutralBorder }}
          >
            Add / Remove
          </Button>
          <Button
            startIcon={<MenuBookIcon />}
            onClick={() => window.open('/app-guide#fno-options', '_blank', 'noopener,noreferrer')}
            size="small"
            variant="outlined"
            sx={{ color: C.accent, borderColor: C.accentBorder }}
          >
            Guide
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
            size="small"
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Search & filter bar */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search symbol…"
          value={searchText}
          onChange={e => setSearchText(e.target.value.toUpperCase())}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
          sx={{ width: 180 }}
        />
        <ToggleButtonGroup
          size="small" exclusive
          value={exchangeFilter}
          onChange={(_, v) => { if (v) setExchangeFilter(v); }}
          sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5, fontSize: '0.72rem', fontWeight: 700 } }}
        >
          {['ALL', 'NSE', 'NFO', 'MCX', 'DELTA'].map(ex => (
            <ToggleButton key={ex} value={ex}>{ex}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small" exclusive
          value={signalFilter}
          onChange={(_, v) => { if (v !== null) setSignalFilter(v); }}
          sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5, fontSize: '0.72rem', fontWeight: 700 } }}
        >
          <ToggleButton value="ALL">All</ToggleButton>
          <ToggleButton value="LONG" sx={{ color: C.bull }}>LONG</ToggleButton>
          <ToggleButton value="SHORT" sx={{ color: C.bear }}>SHORT</ToggleButton>
        </ToggleButtonGroup>
        <Button
          size="small"
          variant={starFilter ? 'contained' : 'outlined'}
          onClick={() => setStarFilter(v => !v)}
          sx={{
            minWidth: 36, px: 1, py: 0.5, fontSize: '0.72rem',
            bgcolor: starFilter ? C.warn : undefined,
            borderColor: C.warnBorder,
            color: starFilter ? C.onSolid : C.warn,
            '&:hover': { bgcolor: starFilter ? C.warn : C.warnMuted }
          }}
          title="Top candidates: symbols with an active LONG/SHORT FNO signal AND score ≥ 70"
        >
          ⭐
        </Button>
        {/* Group chips */}
        <ToggleButtonGroup
          size="small" exclusive
          value={groupFilter}
          onChange={(_, v) => { if (v) setGroupFilter(v); }}
          sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5, fontSize: '0.72rem', fontWeight: 700 } }}
        >
          <ToggleButton value="ALL">All groups</ToggleButton>
          {GROUPS.map(g => (
            <ToggleButton key={g} value={g}>{g}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Tooltip arrow title="Edit per-symbol group assignments (overrides auto-classification)">
          <IconButton size="small" onClick={() => setGroupEditOpen(o => !o)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Group override editor (toggleable) */}
      {groupEditOpen && (
        <Box sx={{ mb: 2, p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Override the auto-assigned group for any symbol. Saved locally.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1 }}>
            {pulseCards.map(c => {
              const current = groupOf(c);
              return (
                <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 80 }}>{c.symbol}</Typography>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <Select
                      value={current}
                      onChange={(e) => saveGroupOverride(c.symbol, e.target.value)}
                      sx={{ fontSize: '0.78rem' }}
                    >
                      {GROUPS.map(g => <MenuItem key={g} value={g} dense>{g}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Pulse Cards row */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.secondary', letterSpacing: 0.5 }}>
        TRACKED UNDERLYINGS {filteredCards.length !== pulseCards.length ? `(${filteredCards.length}/${pulseCards.length})` : `(${pulseCards.length})`}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2, mb: 3 }}>
        {loading && !pulseCards.length ? (
          Array(4).fill(0).map((_, i) => (
            <Skeleton key={i} variant="rectangular" width={280} height={280} sx={{ borderRadius: 2, flexShrink: 0 }} />
          ))
        ) : filteredCards.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              {pulseCards.length === 0
                ? 'No tracked underlyings found.'
                : `${pulseCards.length} tracked underlying${pulseCards.length === 1 ? '' : 's'} loaded, but the current filters exclude all of them.`}
            </Typography>
            {pulseCards.length === 0 ? (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Contact your admin to enable underlyings via <strong>Settings → Options Config</strong>, or check the <strong>Options Guide</strong> for setup instructions.
              </Typography>
            ) : (
              <>
                {/* Show which filters are active so the user knows what to flip. */}
                <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', justifyContent: 'center', mb: 1.2 }}>
                  {searchText && <Chip size="small" label={`Search: "${searchText}"`} onDelete={() => setSearchText('')} />}
                  {exchangeFilter !== 'ALL' && <Chip size="small" label={`Exchange: ${exchangeFilter}`} onDelete={() => setExchangeFilter('ALL')} />}
                  {signalFilter   !== 'ALL' && <Chip size="small" label={`Direction: ${signalFilter}`}   onDelete={() => setSignalFilter('ALL')} />}
                  {groupFilter    !== 'ALL' && <Chip size="small" label={`Group: ${groupFilter}`}        onDelete={() => setGroupFilter('ALL')} />}
                  {starFilter               && <Chip size="small" color="warning" label="★ High conviction only (needs active signal score ≥ 70)" onDelete={() => setStarFilter(false)} />}
                </Box>
                <Button size="small" variant="outlined" onClick={() => {
                  setSearchText(''); setExchangeFilter('ALL'); setSignalFilter('ALL'); setGroupFilter('ALL'); setStarFilter(false);
                }} sx={{ textTransform: 'none' }}>
                  Clear all filters
                </Button>
              </>
            )}
          </Box>
        ) : (
          tieredCards.map(({ card, tier }) => (
            <PulseCard
              key={card.id}
              card={card}
              tier={tier}
              signalScore={
                activeSignal?.underlyingId === card.id ? activeSignal.score : (card.latestSignal?.score ?? undefined)
              }
            />
          ))
        )}
      </Box>

      {/* Signal Card Section */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.secondary', letterSpacing: 0.5 }}>
        SIGNAL CARD — EXECUTION HUB
      </Typography>

      {activeSignal ? (
        <SignalCard
          signal={activeSignal}
          basketId={activeBasket?.id}
          onOpenLab={(symbol) => {
            const sym = symbol || pulseCards.find(c => c.id === activeSignal.underlyingId)?.symbol || '';
            navigate(`/options-lab/${sym}`);
          }}
        />
      ) : (
        <Box sx={{
          p: 4, border: '1px dashed', borderColor: 'divider', borderRadius: 2, textAlign: 'center'
        }}>
          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>No Active Signal</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            The Signal Card will appear here when a TradingView alert is received for a tracked options symbol.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            {pulseCards.slice(0, 3).map(card => (
              <Button
                key={card.id}
                variant="outlined"
                size="small"
                startIcon={<ScienceIcon />}
                onClick={() => navigate(`/options-lab/${card.symbol}`)}
                sx={{ color: C.neutral, borderColor: C.neutralBorder }}
              >
                {card.symbol} Lab
              </Button>
            ))}
          </Box>
        </Box>
      )}

      {/* Quick stats footer */}
      <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
        <StatChip label="Tracked Symbols" value={pulseCards.length} color={C.neutral} />
        <StatChip label="Fresh Data" value={pulseCards.filter(c => !c.dataStale).length} color={C.bull} />
        <StatChip label="Stale Data" value={staleCount} color={staleCount > 0 ? C.bear : C.bull} />
      </Box>

      <ManageSymbolsDialog
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        onChanged={load}
      />
    </Box>
  );
};

const StatChip: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}:</Typography>
    <Chip size="small" label={value}
      sx={{ bgcolor: `${color}22`, color, fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
  </Box>
);

export default OptionsDashboardPage;
